import { Divider, Typography } from '@material-ui/core';
import { eventoFinanceiroDescriptions, pagamentoStatusDescriptions, tipoLiquidacaoDescriptions } from 'common/genericDescriptions';
import {
  PagamentoResponse,
  ParticipanteResumo,
  Fechamento,
  InvestidorResumo,
  PagamentoDocuments,
  PagamentoTipo,
  EventoExtraordinarioTipo,
  PagamentoStatus,
  TipoLiquidacao,
} from 'common/genericTypes';
import { GenericDrawer, GenericDrawerTextContent } from 'components/genericDrawer';
import { SmSpace, NumberField, XlSpace, RadioButton, Dropdown } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { formatDate } from 'utils/date';
import { formatNumber } from 'utils/number';
import ErrorIcon from '@material-ui/icons/Error';
import { labelsCaracteristicas } from 'pages/cra/registro/labels';

enum TipoLancamento {
  Percentual = "PERCENTUAL",
  ValorPagamento = "VALOR_PAGAMENTO",
}

const tipoLancamentoOptions = [
  {
    description: 'Percentual',
    value: TipoLancamento.Percentual
  },
  {
    description: 'Valor de Pagamento',
    value: TipoLancamento.ValorPagamento
  },
];

interface RevisarPagamentoAgenteFiduciarioDrawerProps {
  pagamento?: PagamentoResponse;
  pagamentoDocuments?: Array<PagamentoDocuments>;
  opened: boolean;
  onClose(): void;
  onSubmitSuccess?(): void;
}

interface FechamentoPorCustodiante {
  custodiante: ParticipanteResumo;
  fechamentos: Array<Fechamento>;
  qtdTotal: number;
  valorTotal: number;
}

const RevisarPagamentoAgenteFiduciarioDrawerComponent: React.FC<RevisarPagamentoAgenteFiduciarioDrawerProps> = (props) => {
  const { pagamento: pagamentoProps, opened, onClose, onSubmitSuccess } = props;
  const [pagamento, setPagamento] = useState<PagamentoResponse>();

  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [valorUnitarioPagamento, setValorUnitarioPagamento] = useState<number>(0);
  const [percentual, setPercentual] = useState<number>(0);
  const [valorMulta, setValorMulta] = useState<number>(0);
  const [valorMora, setValorMora] = useState<number>(0);
  const [tipoLancamento, setTipoLancamento] = useState<TipoLancamento>(TipoLancamento.ValorPagamento);
  const [valorPagamentoCalculado, setValorPagamentoCalculado] = useState<number>(0);
  const [percentualCalculado, setPercentualCalculado] = useState<number>(0);
  const [valorUnitarioEmissao, setValorUnitarioEmissao] = useState<number>(0);
  const [puRemanescenteApurado, setPuRemanescenteApurado] = useState<number>(0);
  const [tipoLiquidacao, setTipoLiquidacao] = useState<TipoLiquidacao>(TipoLiquidacao.B3);

  const dispatch = useDispatch();
  const {
    getPagamento: getPagamentoApi,
    putRevisarPUAgenteFiduciario,
  } = useSelector((state: RootState) => state.instrumentoApi);

  const getCalcularAmortizacao = useSelector((state: RootState) => state.instrumentoApi.getCalcularAmortizacao);

  const handleRevisarPagamento = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmarLiquidacao = async () => {
    if (pagamento) {
      try {
        await putRevisarPUAgenteFiduciario(
          dispatch,
          {
            pagamentoId: pagamento.id,
            puRevisado: tipoLancamento === TipoLancamento.Percentual ? valorPagamentoCalculado : valorUnitarioPagamento,
            percentual: pagamento.tipo === PagamentoTipo.Amortizacao && tipoLancamento === TipoLancamento.ValorPagamento ? percentualCalculado : percentual,
            liquidacaoDireta: tipoLiquidacao === TipoLiquidacao.Direta
          },
          true
        );

        setShowConfirmModal(false);
        onSubmitSuccess?.();
        onClose();
      } catch (error) {
        setShowConfirmModal(false);
      }
    }
  };

  const handleCancelarConfirmacao = () => {
    setShowConfirmModal(false);
  };

  const getPagamento = useCallback(async () => {
    setLoading(true);
    if (pagamentoProps !== undefined) {
      try {
        const result = await getPagamentoApi(dispatch, pagamentoProps?.id.toString(), true);
        setPagamento(result);

        if (result && result.status === PagamentoStatus.AguardandoComprovante) {
          setTipoLancamento(TipoLancamento.ValorPagamento);
        }
        setValorUnitarioEmissao(result ? result.instrumentoFinanceiro.valorNominalUnitarioEmissao : 0);
        setValorUnitarioPagamento(result?.status === PagamentoStatus.AguardandoComprovante && result?.valorPagamento ? result.valorPagamento : 0);
        setValorPagamentoCalculado(0);
        setPercentual(result?.status !== PagamentoStatus.AguardandoComprovante && result?.percentual ? result?.percentual : 0);
        setPercentualCalculado(0);
        setValorMulta(result?.valorMulta ?? 0);
        setValorMora(result?.valorMora ?? 0);
        setPuRemanescenteApurado(result?.instrumentoFinanceiro.puRemanescente || 0);

        if (result.fechamentos?.length > 0) {
          let fechamentos: Array<FechamentoPorCustodiante> = [];
          let valorTotalAtivos: number = 0;
          const custodiantesUnicos = Array.from(new Set(result.fechamentos.map((f) => f.custodiante.id)))
            .map((id) => result.fechamentos.find((_) => _.custodiante.id === id)?.custodiante)
            .filter((_) => _ !== undefined)
            .map((_) => _ as ParticipanteResumo);

          const investidoresUnicos = Array.from(new Set(result.fechamentos.map((f) => f.investidor.id)))
            .map((id) => result.fechamentos.find((_) => _.investidor.id === id)?.investidor)
            .filter((_) => _ !== undefined)
            .map((_) => _ as InvestidorResumo);

          for (let i = 0; i < custodiantesUnicos.length; i++) {
            const filtrados = result.fechamentos.filter((_) => _.custodiante.id === custodiantesUnicos[i].id);
            const valorTotal = filtrados.map((_) => _.quantidade * _.precoUnitario).reduce((a, b) => a + b, 0);
            let qtdTotal = 0;

            for (let j = 0; j < investidoresUnicos.length; j++) {
              qtdTotal += filtrados.find((_) => _.investidor.id === investidoresUnicos[j].id)?.quantidade ?? 0;
            }

            fechamentos.push({
              custodiante: custodiantesUnicos[i],
              fechamentos: filtrados,
              valorTotal,
              qtdTotal,
            });

            valorTotalAtivos += valorTotal;
          }
        }
      } catch (error) { }
    }
    setLoading(false);
  }, [dispatch, getPagamentoApi, pagamentoProps]);

  const getCalculoPercentual = useCallback(async () => {
    var request = {
      percentual: undefined,
      valorPagamento: valorUnitarioPagamento,
      valorUnitario: valorUnitarioEmissao,
      puRemanescente: pagamento?.instrumentoFinanceiro.puRemanescente
    };
    try {
      const result = await getCalcularAmortizacao(dispatch, request, false);
      setPercentualCalculado(result.percentual);
      setPuRemanescenteApurado(result.puRemanescenteApurado);
    } catch (error) { }
  }, [valorUnitarioPagamento, tipoLancamento, valorUnitarioEmissao, pagamento]);

  const getCalculoValorPagamento = useCallback(async () => {
    var request = {
      percentual,
      valorPagamento: undefined,
      valorUnitario: valorUnitarioEmissao,
      puRemanescente: pagamento?.instrumentoFinanceiro.puRemanescente
    };
    try {
      const result = await getCalcularAmortizacao(dispatch, request, false);
      setValorPagamentoCalculado(result.valorPagamento);
      setPuRemanescenteApurado(result.puRemanescenteApurado);
    } catch (error) { }
  }, [percentual, tipoLancamento, valorUnitarioEmissao, pagamento]);

  const sugestaoJuros = () => {
    if (pagamento) {
      const percentualRemanescente = pagamento.instrumentoFinanceiro.percentualAmortizacaoRemanescente;
      const puEmitido = pagamento.instrumentoFinanceiro.valorNominalUnitarioEmissao;
      const puAtualizado = pagamento.instrumentoFinanceiro.puAtualizado;
      const puRemanescente = (puEmitido * percentualRemanescente) / 100;
      let juros = puAtualizado ? puAtualizado - puRemanescente : undefined;

      return (
        <>
          <Typography>
            {juros
              ? `Valor Unitário do Juros Previsto: R$ ${juros.toFixed(8)}`
              : 'Sugestão de valor unitário de juros previsto indisponível.'}
            <SmSpace />
          </Typography>
        </>
      );
    }
  };

  const textContents: GenericDrawerTextContent[] = [
    {
      text: `Revisão de Pagamento - Distribuição Pública`,
      header: 'Informações do Pagamento',
    },
    {
      subtitle: 'Instrumento Financeiro',
      text: pagamento?.instrumentoFinanceiro.codigo || '-',
    },
    {
      subtitle: 'Data de Vencimento',
      text: pagamento?.dataVencimento ? formatDate(pagamento.dataVencimento) : '-',
    },
    {
      subtitle: 'Status',
      text: pagamento?.status ? pagamentoStatusDescriptions[pagamento.status] : '-',
    },
    {
      subtitle: 'Tipo',
      text: pagamento?.tipo ? eventoFinanceiroDescriptions[pagamento.tipo] : '-',
    },
    {
      subtitle: 'Total de ativos',
      text: (pagamento?.quantidade || pagamento?.instrumentoFinanceiro.quantidadeDepositada) ?
        formatNumber(pagamento.quantidade ?? pagamento.instrumentoFinanceiro.quantidadeDepositada, { minimumFractionDigits: 0, maximumFractionDigits: 8 }) : "-",
    },
  ];

  const camposEdicao = () => {
    return (
      <>
        <SmSpace />
        <Typography variant="h3">Revisão de Valores</Typography>
        <Divider variant="fullWidth" />
        <SmSpace />

        {(pagamento?.tipo === PagamentoTipo.Amortizacao ||
          pagamento?.tipo === EventoExtraordinarioTipo.Amortizacao) && (
            <>
              <RadioButton
                label={"Tipo de Lançamento"}
                options={tipoLancamentoOptions}
                value={tipoLancamento}
                onChange={(value: string) => setTipoLancamento(value as TipoLancamento)}
                inline
              />
              <SmSpace />
              <NumberField
                label={'Percentual'}
                value={tipoLancamento === TipoLancamento.Percentual ? percentual : percentualCalculado}
                required
                onChange={(value) => tipoLancamento === TipoLancamento.Percentual ? setPercentual(value ?? 0) : null}
                numberType="eightDecimals"
                adornment={'%'}
                adornmentPosition={'end'}
                disabled={tipoLancamento === TipoLancamento.ValorPagamento}
              />
              <SmSpace />
              <NumberField
                label={'Valor Unitário de Pagamento'}
                value={tipoLancamento === TipoLancamento.Percentual ? valorPagamentoCalculado : valorUnitarioPagamento}
                required
                onChange={(value) => tipoLancamento === TipoLancamento.Percentual ? null : setValorUnitarioPagamento(value ?? 0)}
                numberType="eightDecimals"
                disabled={tipoLancamento === TipoLancamento.Percentual}
              />
              <SmSpace />
              <Typography variant='body1'>
                {`Saldo Preço Unitário Remanescente: R$ ${puRemanescenteApurado > 0 ? puRemanescenteApurado.toFixed(8) : (pagamento?.instrumentoFinanceiro.puRemanescente || 0).toFixed(8)}`}
                <SmSpace />
              </Typography>

              {pagamento?.ultimoPagamento && puRemanescenteApurado !== 0 &&
                <div
                  style={{
                    marginBottom: '20px',
                    padding: '20px',
                    border: '2px solid #FFCF40',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <ErrorIcon style={{ marginRight: 10 }} fontSize="small" />
                  <Typography variant="body1">
                    {`ATENÇÃO Evento ${pagamento?.instrumentoFinanceiro.codigo} será liquidado com valor residual. Tem certeza que deseja continuar?`}
                  </Typography>
                </div>
              }
            </>
          )}

        <SmSpace />

        <Dropdown
          label={labelsCaracteristicas.tipoLiquidacao}
          required
          defaultValue={{ value: 'DIRETA', description: 'DIRETA' }}
          options={[
            {
              value: 'DIRETA',
              description: 'DIRETA'
            },
            {
              value: 'B3',
              description: 'BRUTA'
            }
          ]}
          value={tipoLiquidacao}
          onChange={(value) => setTipoLiquidacao(value as TipoLiquidacao)}
        />

        {pagamento?.tipo === PagamentoTipo.Juros && (
          <>
            {sugestaoJuros()}
            <NumberField
              label={'Valor Unitário de Pagamento'}
              value={valorUnitarioPagamento}
              required
              onChange={(value) => setValorUnitarioPagamento(value ?? 0)}
              numberType="eightDecimals"
            />
            <SmSpace />
          </>
        )}

        {pagamento?.inadimplente && (
          <>
            <SmSpace />
            <NumberField
              label={'Multa'}
              value={valorMulta}
              onChange={(value) => setValorMulta(value ?? 0)}
              numberType="eightDecimals"
            />
            <SmSpace />
            <NumberField
              label={'Mora'}
              value={valorMora}
              onChange={(value) => setValorMora(value ?? 0)}
              numberType="eightDecimals"
            />
            <SmSpace />
          </>
        )}

        <Divider variant="fullWidth" />
        <XlSpace />
      </>
    );
  };

  const avisoLiquidacao = () => {
    return (
      <div
        style={{
          marginBottom: '20px',
          padding: '20px',
          border: '2px solid #FFCF40',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <ErrorIcon style={{ marginRight: 10 }} fontSize="small" />
        <Typography variant="body1">
          Ao confirmar/revisar o PU desse Pagamento, ele será automaticamente liquidado. Deseja continuar?
        </Typography>
      </div>
    );
  };

  useEffect(() => {
    if (opened) {
      getPagamento();
    }
  }, [getPagamento, opened]);

  useEffect(() => {
    if (pagamento && pagamento?.status === PagamentoStatus.AguardandoComprovante) setTipoLancamento(TipoLancamento.ValorPagamento);
    setValorUnitarioEmissao(pagamento ? pagamento.instrumentoFinanceiro.valorNominalUnitarioEmissao : 0);
    setValorUnitarioPagamento(pagamento?.status === PagamentoStatus.AguardandoComprovante && pagamento?.valorPagamento ? pagamento.valorPagamento : 0);
    setValorPagamentoCalculado(0);
    setPercentual(pagamento?.status !== PagamentoStatus.AguardandoComprovante && pagamento?.percentual ? pagamento?.percentual : 0);
    setPercentualCalculado(0);
    setValorMulta(pagamento?.valorMulta ?? 0);
    setValorMora(pagamento?.valorMora ?? 0);
    setPuRemanescenteApurado(pagamento?.instrumentoFinanceiro.puRemanescente || 0);
  }, [opened, pagamento]);

  useEffect(() => {
    if (opened && tipoLancamento === TipoLancamento.ValorPagamento && valorUnitarioPagamento > 0)
      getCalculoPercentual();
  }, [valorUnitarioPagamento, tipoLancamento, opened, pagamento, getCalculoPercentual]);

  useEffect(() => {
    if (opened && tipoLancamento === TipoLancamento.Percentual && percentual > 0)
      getCalculoValorPagamento();
  }, [percentual, tipoLancamento, opened, pagamento, getCalculoValorPagamento]);

  useEffect(() => {
    if (opened && pagamento && valorUnitarioEmissao > 0) {
      if (tipoLancamento === TipoLancamento.ValorPagamento && valorUnitarioPagamento > 0) {
        getCalculoPercentual();
      } else if (tipoLancamento === TipoLancamento.Percentual && percentual > 0) {
        getCalculoValorPagamento();
      }
    }
  }, [opened, pagamento, valorUnitarioEmissao, tipoLancamento, valorUnitarioPagamento, percentual, getCalculoPercentual, getCalculoValorPagamento]);

  const modalConfirmacao = (
    <GenericDrawer
      opened={showConfirmModal}
      onClose={handleCancelarConfirmacao}
      title={'Confirmação de Liquidação'}
      textContents={[
        {
          text: 'Ao confirmar/revisar o PU desse Pagamento, ele será automaticamente liquidado. Deseja continuar?'
        },
        {
          subtitle: 'PU Revisado',
          text: `R$ ${(tipoLancamento === TipoLancamento.Percentual ? valorPagamentoCalculado : valorUnitarioPagamento).toFixed(8)}`
        },
        {
          subtitle: 'Pagamento ID',
          text: pagamento?.id || '-'
        },
      ]}
      buttons={[
        {
          buttonText: 'Sim, Liquidar',
          buttonAction: handleConfirmarLiquidacao,
          buttonColor: 'primary',
        },
        {
          buttonText: 'Cancelar',
          buttonAction: handleCancelarConfirmacao,
        },
      ]}
    />
  );

  return (
    <>
      <GenericDrawer
        opened={opened && !showConfirmModal}
        title={'Revisar Pagamento - Agente Fiduciário'}
        textContents={!loading ? textContents : []}
        onClose={onClose}
        buttons={[
          {
            buttonText: 'Revisar e Liquidar',
            buttonAction: handleRevisarPagamento,
            buttonColor: 'primary',
          },
          {
            buttonText: 'Cancelar',
            buttonAction: onClose,
          },
        ]}
      >
        {camposEdicao()}
        {avisoLiquidacao()}
      </GenericDrawer>
      {modalConfirmacao}
    </>
  );
};

export const RevisarPagamentoAgenteFiduciarioDrawer = RevisarPagamentoAgenteFiduciarioDrawerComponent;
